import { useState, type FormEvent } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/dafulc3ep053ijub1vu0";

type Status = { state: "idle" | "submitting" | "success" | "error"; msg: string };

export default function Subscribe() {
  const [status, setStatus] = useState<Status>({ state: "idle", msg: "" });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // honeypot: if filled, silently "succeed" without sending
    const honeypot = String(fd.get("phone_alt") ?? "").trim();
    if (honeypot) {
      setStatus({ state: "success", msg: "订阅成功！已把你加入热搜快报名单。" });
      form.reset();
      return;
    }

    const email = String(fd.get("email") ?? "").trim();
    if (!email) {
      setStatus({ state: "error", msg: "请先填写正确的邮箱地址。" });
      return;
    }

    const payload = new URLSearchParams();
    payload.set("email", email);

    setStatus({ state: "submitting", msg: "" });
    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      if (res.ok && code === "OK") {
        setStatus({ state: "success", msg: "订阅成功！我们会第一时间把风评风向送到你邮箱。" });
        form.reset();
      } else {
        const shown = serverMsg && !/spam/i.test(serverMsg) ? serverMsg : "订阅失败，请稍后再试。";
        setStatus({ state: "error", msg: shown });
      }
    } catch {
      setStatus({ state: "error", msg: "网络异常，请稍后重试。" });
    }
  };

  return (
    <section id="subscribe" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="overflow-hidden rounded-3xl bg-accent-500">
          <div className="grid grid-cols-1 items-center lg:grid-cols-2">
            <div className="p-7 md:p-10">
              <span className="inline-flex items-center gap-2 rounded-full bg-background-950/10 px-3 py-1 text-xs font-medium text-foreground-950">
                <i className="ri-mail-send-line" aria-hidden="true" />
                热搜快报 · 免费订阅
              </span>
              <h2 className="font-heading mt-4 text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
                想第一时间吃到哇偶姐姐的瓜？
              </h2>
              <p className="mt-3 text-sm leading-6 text-foreground-900/80">
                订阅我们的热搜快报，每有新争议、新热评或风评转向，我们都用一封简洁的邮件帮你梳理清楚，吃瓜不迷路也不落伍。
              </p>
              <ul className="mt-5 space-y-2 text-sm text-foreground-900/80">
                <li className="flex items-center gap-2"><i className="ri-check-double-line text-foreground-950" aria-hidden="true" />风评转向第一时间提醒</li>
                <li className="flex items-center gap-2"><i className="ri-check-double-line text-foreground-950" aria-hidden="true" />每周争议焦点复盘</li>
                <li className="flex items-center gap-2"><i className="ri-check-double-line text-foreground-950" aria-hidden="true" />随时退订，无骚扰</li>
              </ul>
            </div>

            <div className="p-7 pt-0 md:p-10 lg:pt-10">
              <form onSubmit={handleSubmit} data-readdy-form className="rounded-2xl bg-background-50 p-5 md:p-7">
                <label htmlFor="newsletter-email" className="text-sm font-medium text-foreground-900">
                  你的邮箱
                </label>
                <input
                  id="newsletter-email"
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  placeholder="name@example.com"
                  className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="melba-fields"
                />

                <button
                  type="submit"
                  disabled={status.state === "submitting"}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status.state === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true" /> 正在订阅…
                    </>
                  ) : (
                    <>
                      <i className="ri-arrow-right-line" aria-hidden="true" /> 订阅热搜快报
                    </>
                  )}
                </button>

                {status.state === "success" && (
                  <p className="mt-4 rounded-lg bg-primary-50 p-3 text-sm text-primary-700">
                    <i className="ri-checkbox-circle-line mr-1" aria-hidden="true" />
                    {status.msg}
                  </p>
                )}
                {status.state === "error" && (
                  <p className="mt-4 rounded-lg bg-secondary-100 p-3 text-sm text-secondary-900">
                    <i className="ri-error-warning-line mr-1" aria-hidden="true" />
                    {status.msg}
                  </p>
                )}
              </form>
              <p className="mt-3 px-1 text-xs text-foreground-900/60">
                订阅即表示你同意接收围观站内容，我们不会泄露你的邮箱给任何第三方。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}